import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';
import Field, { controlClass, type ControlSize } from './Field';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  hint?: string;
  error?: string;
  /** Control height ('sm' 28 / 'md' 32 / 'lg' 36). A number is passed through
   *  as the native HTML `size` attribute for backward compatibility. */
  size?: ControlSize | number;
  options: SelectOption[];
  /** Renders a leading empty-value option. */
  placeholder?: string;
}

/** Styled native <select> — same value/onChange contract as before. */
const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, size = 'lg', options, placeholder, className, id, ...rest },
  ref,
) {
  const controlSize: ControlSize = typeof size === 'string' ? size : 'lg';
  const htmlSize = typeof size === 'number' ? size : undefined;
  return (
    <Field label={label} hint={hint} error={error} required={required} id={id}>
      {(ids) => (
        <div className="relative">
          <select
            ref={ref}
            id={ids.id}
            aria-describedby={ids.describedBy}
            aria-invalid={ids.invalid || undefined}
            required={required}
            size={htmlSize}
            className={cn(controlClass(controlSize, ids.invalid), 'appearance-none pr-9 cursor-pointer', className)}
            {...rest}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint"
          />
        </div>
      )}
    </Field>
  );
});

export default Select;
